import { stripAeoMarkdownBodies } from 'src/lib/aeo-markdown';
import type { Entry, Sys } from 'src/types/contentful-sdk-compat';
import { NextPage } from 'next';
import {
  BLOG_ENTRIES_PER_PAGE,
  CmsThemes,
  ContentfulTemplates,
  isTesting,
} from 'src/lib/constants';
import { blogCmsClient } from 'src/lib/contentful-client';
import { fetchTagsWithMinPosts } from 'src/lib/blog-tag-helpers';
import { optimizeContentfulImages } from 'src/lib/image-helpers';
import { BlogOverviewTemplate } from 'src/templates/BlogOverviewTemplate';

interface PageMetaFields {
  heading: string;
  metaDescription?: string;
  metaTitle: string;
}

interface BlogPost {
  fields: PageMetaFields;
  sys: Sys;
}

interface Fields {
  metaTitle: string;
  heading: string;
  metaDescription: string;
  pageSlug: string;
}

interface IndexProps {
  fields: Fields;
  postsCount: number;
  pageCount: number;
  posts: Entry<BlogPost>[];
  allTags: string[];
  tag: string;
}

const TagIndex: NextPage<IndexProps> = ({
  fields,
  posts,
  pageCount,
  allTags,
  tag,
}) => {
  return (
    <BlogOverviewTemplate
      fields={fields}
      posts={posts}
      heading={fields.heading}
      pageNum={1}
      totalPages={pageCount}
      basePath={`/blog/tag/${tag}`}
      activeTag={tag}
      allTags={allTags}
      robotsContent='noindex, follow'
      cmsTheme={CmsThemes.WEB3}
    />
  );
};

// Static build: uses getStaticPaths to pre-generate all tag pages at build time
export async function getStaticPaths() {
  let tags;
  try {
    tags = await blogCmsClient.getEntries({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      content_type: 'tag',
    });
  } catch (e) {
    throw new Error(e);
  }

  const paths = tags.items.reduce((prev, { fields }) => {
    if (fields['tag']) {
      return [...prev, { params: { tag: fields['tag'] } }];
    }
    return prev;
  }, []);

  return {
    paths: isTesting ? [] : paths,
    fallback: false,
  };
}

const fetchTagId = async (tag) => {
  let res;
  try {
    res = await blogCmsClient.getEntries({
      content_type: 'tag', // eslint-disable-line
      'fields.tag[in]': [tag],
    });
  } catch (e) {
    throw new Error(e);
  }
  const {
    items: [matchingTag],
  } = res;
  const {
    sys: { id },
  } = matchingTag;
  return id;
};

// Static build: uses getStaticProps for build-time data fetching
export async function getStaticProps({ params, preview }) {
  const { tag } = params;
  let tagId;
  try {
    tagId = await fetchTagId(tag);
  } catch (e) {
    throw new Error(e);
  }

  let entries: Record<string, unknown> | unknown = blogCmsClient.getEntries({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    content_type: ContentfulTemplates.BLOG_POST,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    links_to_entry: tagId,
    include: 10,
    skip: 0,
    limit: BLOG_ENTRIES_PER_PAGE,
    order: ['-fields.published'],
  });

  let overview: Record<string, unknown> | unknown = blogCmsClient.getEntries({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    content_type: ContentfulTemplates.BLOG_OVERVIEW,
    include: 10,
  });

  let allTags: string[];
  try {
    [overview, entries, allTags] = await Promise.all([
      overview,
      entries,
      fetchTagsWithMinPosts(),
    ]);
  } catch (e) {
    throw new Error(e);
  }

  const fields = overview['items'][0].fields as Fields;
  const posts = entries['items'];

  const postsCount = entries['total'];
  const pageCount = Math.ceil(postsCount / BLOG_ENTRIES_PER_PAGE);

  return {
    props: stripAeoMarkdownBodies({
      fields,
      posts: optimizeContentfulImages(posts),
      postsCount,
      pageCount,
      allTags,
      tag,
      preview: !!preview,
    }),
  };
}

export default TagIndex;
